import { CellContext, Row } from "@tanstack/react-table";
import { JSX } from "react";

export type CellContextComponent<TData> = React.FC<CellContext<TData, unknown>>;

/**
 * Generates a callback function to render a cell using a JSX element from a different property of the row data.
 * The generated function can be passed as the `cell` property value in a `react-table` column definition. This is useful
 * when one property is used as the column accessor (i.e for sorting) and another property holds the rendered element for the cell.
 *
 * @example
 * ```tsx
 * columnHelper.accessor("normalizedLine.description", {
 *   cell: renderRowPropNode((row) => row.normalizedLine.descriptionElement),
 *   ...
 * })
 * ```
 */
export function renderRowPropElement<TData>(
  getCellElement: (rowData: TData) => React.ReactNode,
): (cell: CellContext<TData, unknown>) => JSX.Element {
  // eslint-disable-next-line react/display-name
  return (cell) => <>{getCellElement(cell.row.original)}</>;
}

/**
 * Generates a callback function to render a cell using a React component from a different property of the row data.
 * The generated function can be passed as the `cell` property value in a `react-table` column definition. This is useful
 * when one property is used as the column accessor (i.e for sorting) and another property holds a component used to render the cell.
 *
 * @example
 * ```tsx
 * columnHelper.accessor("normalizedLine.bin.number", {
 *   cell: renderRowPropComponent((row) => row.normalizedLine.BinComponent),
 *   ...
 * })
 * ```
 */
export function renderRowPropComponent<TData>(
  getCellComponent: (rowData: TData) => CellContextComponent<TData>,
): (cell: CellContext<TData, unknown>) => JSX.Element {
  // eslint-disable-next-line react/display-name
  return (cell) => {
    const CellComponent = getCellComponent(cell.row.original);
    return <CellComponent {...cell} />;
  };
}

/**
 * Gets a sorting function that can be used for a table that displays a counting progress bar.
 *
 */
export function getCountingBarSortFunction<TData>(
  getNow: (rowData: TData) => number,
  getMax: (rowData: TData) => number,
): (a: Row<TData>, b: Row<TData>, columnId: string) => number {
  return (a, b) => {
    const nowA = getNow(a.original);
    const maxA = getMax(a.original);
    const nowB = getNow(b.original);
    const maxB = getMax(b.original);

    // Calculate the percentage of 'now' to 'max' for both rows
    // If max is 0 and now is also 0, we treat it as the minimum value
    // If max is 0 and now is non-zero, we treat it as the maximum value
    const percentA =
      maxA ? nowA / maxA
      : nowA ? 2
      : -2;
    const percentB =
      maxB ? nowB / maxB
      : nowB ? 2
      : -2;

    // Sort by percentage first, then by 'now' value, then by 'max' value
    // NOTE: The last tie-breaker is only used for rows where 'max' is 0 (i.e. sorting 0/1 vs 0/2)
    const sort = percentA - percentB || nowA - nowB || maxA - maxB;

    // With react-table, it is not good enough to return a value > 0 or < 0, we need to return exactly -1, 0, or 1
    // See: https://tanstack.com/table/latest/docs/guide/sorting#custom-sorting-functions
    return (
      sort > 0 ? 1
      : sort < 0 ? -1
      : 0
    );
  };
}
